import React from 'react'
import { Guardian, Student, Teacher } from '../../providers/context'
import { Loader } from '../../components/simple/loader'
import { useRequest } from '../../request'
import { InboxTab } from './inboxes'
import { Avatar } from '../../components/avatar'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { MessageBase } from './message'
import { useTranslation } from '../../providers/translation'
import { useSmartLinkTo } from '../../components/simple/link'
import { groupByKeys } from '../../utils'
import { ConferenceLocation } from '../ptc/components/conferenceComponents'
import { MiniPie, MiniPieData } from '../../components/charts/miniPie'

type Conference = {
  id: number
  name: string
  date: string
  school: string
  pie?: MiniPieData
}

interface ConferenceProps {
  user: Teacher
  inboxTab: { tab: InboxTab }
}

export const Conferences: React.FC<ConferenceProps> = ({ user, inboxTab }) => {
  const { data: conferences, loading } = useRequest<{ current: Conference[]; past: Conference[] }>(
    '/frontend_api/getConferences',
    inboxTab // passed in as data to make it reload when the tab changes (tab changes indicates a reload is needed)
  )

  return (
    <div
      className={`flex-grow overflow-y-auto overflow-x-hidden bg-slate-50 px-[.375rem] ${conferences && loading ? 'pointer-events-none opacity-50' : ''}`}
    >
      {!conferences ? (
        <Loader />
      ) : (
        <>
          <div className="mb-2 mt-3 flex justify-center gap-8 text-center">
            <b className="text-lg">Your Conferences</b>
            {user.PTCAdmin && (
              <Button logo small link="/ptc/newConference">
                New Conference
              </Button>
            )}
          </div>
          {conferences.current.length === 0 && <div className="mt-4 text-center">No Current Conferences</div>}
          {conferences.current.map((conference) => (
            <Conference conference={conference} key={conference.id} showPie={true} />
          ))}
          {conferences.past.length > 0 && (
            <>
              <div className="mb-2 mt-3 text-center text-lg font-bold">Conference History</div>
              <div className="opacity-50">
                {conferences.past.map((conference) => (
                  <Conference conference={conference} key={conference.id} showPie={true} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

const Conference: React.FC<{ conference: Conference; showPie?: boolean }> = ({ conference, showPie = true }) => {
  const smartLinkTo = useSmartLinkTo()
  return (
    <MessageBase
      onClick={smartLinkTo(`/ptc/conference/${conference.id}`)}
      avatar={{
        color: '#2295f0',
        img: 'icon-school',
      }}
      top={
        <>
          <div className="flex-1 font-bold">{conference.name}</div>
          <div className="ml-auto flex-shrink-0 font-semibold opacity-60">{conference.date}</div>
          {showPie && conference.pie && <MiniPie className="ml-2" data={conference.pie} />}
        </>
      }
      bottom={
        <>
          <div className="text-sm font-bold opacity-50">{conference.school}</div>
        </>
      }
    />
  )
}

type Invite = Conference & {
  student: string
  teacher: string
  class?: string
}

type Appointment = Invite & {
  appointmentTime: string
  length: string
  location: ConferenceLocation
  virtual: boolean
}

interface ConferenceInviteProps {
  user: Student | Guardian
  inboxTab?: { tab: InboxTab }
  initialStudentID?: number
}

export const ConferenceInvites: React.FC<ConferenceInviteProps> = ({ user, inboxTab, initialStudentID }) => {
  const { data: conferences, loading } = useRequest<{ invites: Invite[]; appointments: Appointment[] }>(
    '/frontend_api/getConferenceInvites',
    inboxTab // passed in as data to make it reload when the tab changes (tab changes indicates a reload is needed)
  )

  const { tr } = useTranslation('ptc')

  const allStudents = React.useMemo(
    () => [
      ...new Set([
        ...(conferences?.invites.map((invite) => invite.student) || []),
        ...(conferences?.appointments.map((appointment) => appointment.student) || []),
      ]),
    ],
    [conferences]
  )

  const invitesByStudent = groupByKeys(conferences?.invites || [], ['student'])
  const appointmentsByStudent = groupByKeys(conferences?.appointments || [], ['student'])

  const studentWithTheMostInvites = Object.entries(invitesByStudent).reduce(
    (acc, [student, invites]) => (invites.length > acc[1] ? ([student, invites.length] as [string, number]) : acc),
    ['', 0] as [string, number]
  )[0]

  const initialStudent =
    user.type === 'guardian'
      ? allStudents.find((s) => s === user.children.find((student) => student.id === initialStudentID)?.name)
      : undefined

  const [selectedStudent, setSelectedStudent] = React.useState(studentWithTheMostInvites || allStudents[0])
  React.useEffect(
    () => setSelectedStudent(initialStudent || studentWithTheMostInvites || allStudents[0]),
    [studentWithTheMostInvites, allStudents]
  )

  const invitesForStudent = invitesByStudent[selectedStudent] || []
  const appointmentsForStudent = appointmentsByStudent[selectedStudent] || []

  const invitesGroupedByConference = Object.values(groupByKeys(invitesForStudent, ['school', 'name']))
  const appointmentsGroupedByConference = Object.values(groupByKeys(appointmentsForStudent, ['school', 'name']))

  const [invitesTabSelected, setInvitesTabSelected] = React.useState(true)
  React.useEffect(() => setInvitesTabSelected(!!invitesForStudent.length), [conferences, selectedStudent])

  return (
    <div
      className={`flex flex-grow flex-col gap-2 overflow-y-auto overflow-x-hidden px-6 py-4 ${conferences && loading ? 'pointer-events-none opacity-50' : ''}`}
    >
      {!conferences ? (
        <Loader />
      ) : (
        <>
          {allStudents.length > 1 && (
            <div className="-m-1 flex flex-shrink-0 gap-4 overflow-x-auto text-nowrap p-1">
              {allStudents.map((student) => (
                <div
                  key={student}
                  className={`select-none pb-1 font-semibold ${selectedStudent === student ? 'border-b-2 border-logo text-logo' : 'cursor-pointer text-neutral-600'}`}
                  onClick={() => setSelectedStudent(student)}
                >
                  {student} ({(invitesByStudent[student] || []).length + (appointmentsByStudent[student] || []).length})
                </div>
              ))}
            </div>
          )}
          <div className="-m-1 mt-1 flex gap-2 p-1">
            <Button
              small
              className={`${invitesTabSelected ? 'font-semibold' : ''}`}
              buttonStyle={invitesTabSelected ? ButtonStyle.blueLight : ButtonStyle.light}
              onClick={() => setInvitesTabSelected(true)}
            >
              {tr('Invites')} ({invitesForStudent.length})
            </Button>
            <Button
              small
              className={`${!invitesTabSelected ? 'font-semibold' : ''}`}
              buttonStyle={!invitesTabSelected ? ButtonStyle.blueLight : ButtonStyle.light}
              onClick={() => setInvitesTabSelected(false)}
            >
              {tr('Scheduled Conferences')} ({appointmentsForStudent.length})
            </Button>
          </div>
          {invitesTabSelected ? (
            <>
              {invitesGroupedByConference.map((conferenceInvites) => (
                <React.Fragment key={conferenceInvites[0].name}>
                  <ConferenceLabel conference={conferenceInvites[0]} />

                  {conferenceInvites.map((invite) => (
                    <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-2 shadow" key={invite.id}>
                      <Avatar className="mt-1 self-start" src={{ color: '#3b82f6', img: 'icon-clock' }} />
                      <div className="flex min-w-0 flex-grow flex-col">
                        <b>{invite.teacher}</b>
                        {invite.class && <div>{invite.class}</div>}
                      </div>
                      <Button
                        shape={ButtonShape.rounded}
                        buttonStyle={ButtonStyle.blue}
                        link={`/ptc/inviteResponse/${invite.id}`}
                      >
                        {tr('Schedule')}
                      </Button>
                    </div>
                  ))}
                </React.Fragment>
              ))}
              {invitesGroupedByConference.length === 0 && (
                <div className="mt-2 flex">
                  {tr('No conference invites for')} {selectedStudent}
                </div>
              )}
            </>
          ) : (
            <>
              {appointmentsGroupedByConference.map((conferenceAppointments) => (
                <React.Fragment key={conferenceAppointments[0].name}>
                  <ConferenceLabel conference={conferenceAppointments[0]} />
                  {conferenceAppointments.map((appointment) => (
                    <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-2 shadow" key={appointment.id}>
                      <Avatar className="mt-1 self-start" src={{ color: '#3b82f6', img: 'icon-calendar-check' }} />
                      <div className="flex min-w-0 flex-grow flex-col">
                        <b>{appointment.teacher}</b>
                        {appointment.class && <div className="-mt-1 opacity-50">{appointment.class}</div>}
                        <div>{appointment.appointmentTime}</div>
                        <ConferenceLocation
                          location={appointment.location}
                          virtualOrInPerson={appointment.virtual ? 'virtual' : 'inperson'}
                        />
                      </div>
                      <Button shape={ButtonShape.rounded} link={`/ptc/inviteResponse/${appointment.id}`}>
                        {tr('Edit')}
                      </Button>
                    </div>
                  ))}
                </React.Fragment>
              ))}
              {appointmentsGroupedByConference.length === 0 && (
                <div className="mt-2 flex">
                  {tr('No scheduled conferences for')} {selectedStudent}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

const ConferenceLabel: React.FC<{ conference: Invite }> = ({ conference }) => (
  <>
    <b className="mt-4 text-lg">{conference.date}</b>
    <div className="-mt-1">{conference.school}</div>
    <div className="-mt-2 mb-2">{conference.name}</div>
  </>
)
